import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CalendarRange, Handshake, Layers, Settings2, Users, Wallet } from 'lucide-react';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useBrokerMonthlyReport } from '../../../hooks/pipelineTracker/useBrokerMonthlyReport';
import { useRemSettings } from '../../../hooks/pipelineTracker/useRemSettings';
import { canEditTaxonomy } from '../../../hooks/pipelineTracker/reportContract';
import { formatCents, formatIsoDate } from '../../../hooks/pipelineTracker/usePipelineReport';
import {
  ALL_BROKERS_OPTION,
  COMMISSION_LINKS,
  adviserKeyFromOption,
  brokerOptionValue,
  monthShort,
  type BrokerMonthlyPayload,
  type BrokerMonthsKind,
  type BrokerMonthsRow,
  type LoanTypeBreakdown,
  type LoanTypeRow,
  type RemBrokerCost,
} from '../../../hooks/pipelineTracker/remContract';
import { ComingSoonList, Honesty, Pill, money, num, type ComingSoonItem } from './pipelineParts';
import { BandTag, bandColour, bandRangeLabel, pct } from './remParts';
import RemSettingsEditor from './RemSettingsEditor';
import { SECTION_COLOUR, TrackerSection } from './TrackerSection';
import { StatCard, StatGrid } from './trackerUi';
import { AccentPanel, RatioBar } from './opsUi';
import { InfoTip } from './trackerKit';

/**
 * Broker REM: settlement volume banding, loan type breakdown and broker costs, from the tracker's
 * own data. No commission figure is computed here; the banding panel points at Commission Settings.
 *
 * THE RULE, as Matt Spears (Evoke) confirmed on 21 Sep 2026: a broker sits in ONE band per calendar
 * month, set by their own settled volume, and bands reset every month with no carry-over. The band's
 * percentage is that broker's split of their OWN net commission for the month, applied to the whole
 * month's commission (not marginal, not only the volume above a threshold). There is no shared pot:
 * three bands reading 40%, 50% and 60% are three possible splits, not shares of one sum (founder,
 * 5 Oct 2026, after "of the pool" read as one pot split past 100%). This tab can say which band each
 * month reached and what split that band pays. It cannot say what the split was worth: the broker's
 * net commission is commission data, outside the tracker's isolation fence, so the payout is a link
 * and never a number computed here.
 *
 * ROI (which no VALUTTEN page computes yet) is named in the tab's "Coming when the data is
 * available" list rather than drawn as an empty card (founder, 1 Oct 2026). The strip of links to
 * the commission pages that sat under the banding panel was removed (founder, 5 Oct 2026).
 */

/** Broker REM's sections that cannot be shown yet. */
export const BROKER_REM_COMING_SOON: ReadonlyArray<ComingSoonItem> = [
  {
    section: 'ROI % by broker',
    needs: 'Revenue earned per broker, set against the broker costs entered here. Revenue is commission data, which stays on the commission pages and is not joined to the tracker.',
    from: 'VALUTTEN, in a later build',
  },
];

/**
 * True when settled deals exist but neither table has a single recorded or Mixed row: every deal is
 * "Not recorded" or "No product split" in both. Two tables of nothing read as a broken section, so
 * the breakdown is named in the tab's "Coming when the data is available" list instead (founder,
 * 1 Oct 2026). A breakdown with any recorded row is drawn as it stands.
 */
export const loanTypeBreakdownIsBlank = (lt: LoanTypeBreakdown): boolean =>
  lt.status === 'ok'
  && lt.settledDeals > 0
  && [...lt.byPurpose, ...lt.byTransactionType].every((r) => r.basis === 'not_recorded' || r.basis === 'no_split');

/** The Loan type breakdown's line in the Coming list, when it has nothing to draw. */
export const LOAN_TYPE_WAITING_ITEM: ComingSoonItem = {
  section: 'Loan type breakdown',
  needs: 'Loan purpose and transaction type on the settled deals\' product splits. The Sales export carries none for these deals.',
  from: 'Evoke',
};

const TH = 'px-2.5 py-2 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-gray-400';
const TD = 'px-2.5 py-2 tabular-nums';

/** Selector groups, in order. The Unassigned bucket is not a person and is not selectable. */
const SELECTOR_GROUPS: ReadonlyArray<{ kind: BrokerMonthsKind; label: string }> = [
  { kind: 'broker', label: 'Brokers' },
  { kind: 'group', label: 'Group members' },
  { kind: 'support', label: 'Support staff' },
];

/** A row's roster kind. A payload from before kinds existed reads every row as a broker. */
const kindOf = (row: { kind?: BrokerMonthsKind }): BrokerMonthsKind => row.kind ?? 'broker';

/**
 * Each group's colour, by what it holds rather than its step number: Costs is "2" or "3" depending
 * on whether Loan types shows, and a step colour shifted with it (founder, 6 Oct 2026: a violet
 * "3 · Costs" over an amber panel). Banding keeps the scheme's violet, Loan types the cyan of its
 * transaction-type bars, Costs amber; neighbours never share one.
 */
const REM_ACCENT = { banding: SECTION_COLOUR.violet, loanTypes: SECTION_COLOUR.cyan, costs: SECTION_COLOUR.amber };

const BrokerRemTab: React.FC<{ companyId: string; role?: string }> = ({ companyId, role }) => {
  const [adviserKey, setAdviserKey] = useState('');
  const [editing, setEditing] = useState(false);
  const report = useBrokerMonthlyReport(companyId, adviserKey ? { adviserKey } : {});
  const rem = useRemSettings(companyId);
  const canEdit = canEditTaxonomy(role);

  const response = report.response;
  const data: BrokerMonthlyPayload | null = response && response.status === 'ok' ? response : null;
  // People a cost row can name: never the Unassigned bucket, which is not a person.
  const people = useMemo(() => (data?.advisers ?? []).filter((a) => kindOf(a) !== 'unassigned'), [data]);
  const loanTypesBlank = data !== null && loanTypeBreakdownIsBlank(data.loanTypes);
  const comingSoon: ComingSoonItem[] = loanTypesBlank
    ? [LOAN_TYPE_WAITING_ITEM, ...BROKER_REM_COMING_SOON]
    : [...BROKER_REM_COMING_SOON];
  // Groups are numbered in the order they render, so a hidden Loan types group leaves no gap.
  const showLoanTypes = data !== null && !loanTypesBlank;
  const costsStep = showLoanTypes ? 3 : 2;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-700/70 bg-gradient-to-r from-blue-500/[0.08] via-gray-800/40 to-gray-800/40 px-4 py-3">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15 text-blue-400"
        >
          <Users className="h-4 w-4" />
        </span>
        <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Broker
          <select
            aria-label="Broker"
            value={adviserKey === '' ? ALL_BROKERS_OPTION : brokerOptionValue(adviserKey)}
            onChange={(e) => setAdviserKey(adviserKeyFromOption(e.target.value))}
            className="rounded-lg border border-gray-600 bg-gray-900/80 px-3 py-1.5 text-sm normal-case tracking-normal text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={ALL_BROKERS_OPTION}>All brokers</option>
            {SELECTOR_GROUPS.map((group) => {
              const members = people.filter((a) => kindOf(a) === group.kind);
              if (members.length === 0) return null;
              return (
                <optgroup key={group.kind} label={group.label}>
                  {members.map((a) => (
                    <option key={a.adviserKey} value={brokerOptionValue(a.adviserKey)}>{a.adviserName}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </label>
        {data && (
          <span className="text-xs text-gray-400">
            Settled volume from the {formatIsoDate(data.asAtDate)} Performance export. Commission
            figures stay on their own VALUTTEN pages and are not calculated here.
          </span>
        )}
      </div>

      {report.error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          <AlertCircle className="h-4 w-4" /> {report.error}
        </div>
      )}
      {report.loading && !data && <SkeletonTable />}
      {response && response.status === 'no_snapshot' && (
        <Honesty>No Performance export has been published yet. Upload one to see settlement volume by broker.</Honesty>
      )}

      <TrackerSection
        id="rem-banding"
        step="1 · Banding"
        accent={REM_ACCENT.banding}
        title="Which band each broker reached"
        description="Each broker's band for the month, from their own settled volume."
      >
      {data && <HeadlineCards data={data} />}

      <AccentPanel
        title="Settlement volume banding"
        icon={Layers}
        color="#7c3aed"
        badge={<Pill tone="brand">Tracker data</Pill>}
        blurb={
          <>
            {/* How a band applies is said once, under the band cards (BAND_RULE), not here as well. */}
            Volume is the deal value of deals that settled in the month, dated by the Settled
            column or, where the export has none, by the Current Status Date of a Settled deal: the
            same rule as Deal movements. Only people the broker roster lists as brokers are banded;
            support staff, group members and deals with no owner are listed apart and not banded.
          </>
        }
      >
        <div className="space-y-4">
          {canEdit && (
            <div>
              {!editing ? (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  disabled={!rem.settings}
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-100 transition-colors hover:bg-violet-500/20 disabled:opacity-50"
                >
                  <Settings2 className="h-4 w-4" /> Edit bands and costs
                </button>
              ) : rem.settings && (
                <RemSettingsEditor
                  bands={rem.settings.bands}
                  brokerCosts={rem.settings.brokerCosts}
                  people={people}
                  saving={rem.saving}
                  onSave={async (bands, costs) => {
                    const out = await rem.save(bands, costs);
                    if (out.ok) void report.reload();
                    return out;
                  }}
                  onClose={() => setEditing(false)}
                />
              )}
              {rem.error && <p className="mt-2 text-xs text-amber-300">{rem.error}</p>}
            </div>
          )}

          {data && <BandCards data={data} />}
          {data && <BandProgress data={data} />}
          {data && data.bands.length > 0 && <BandingTable data={data} />}

          <p className="text-xs leading-relaxed text-gray-400">
            What a band paid in dollars depends on that broker&apos;s own net commission for the month,
            which comes from your commission files. It is not calculated here; see{' '}
            <Link to={COMMISSION_LINKS.commissionSettings.to} className="font-semibold text-blue-300 hover:text-blue-200">
              {COMMISSION_LINKS.commissionSettings.label}
            </Link>
            .
          </p>
        </div>
      </AccentPanel>
      </TrackerSection>

      {showLoanTypes && data && (
        <TrackerSection
          id="rem-loan-types"
          step="2 · Loan types"
          accent={REM_ACCENT.loanTypes}
          title="What settled, by loan purpose and transaction type"
          description="Settled deals by the loan purpose and transaction type on their product splits in the Sales export."
        >
          <LoanTypePanel data={data} />
        </TrackerSection>
      )}

      <TrackerSection
        id="rem-costs"
        step={`${costsStep} · Costs`}
        accent={REM_ACCENT.costs}
        title="What each broker costs per month"
        description="The practice's monthly base and support cost per broker, as entered."
      >
        <CostsPanel
          costs={rem.settings?.brokerCosts ?? null}
          people={people}
          canEdit={canEdit}
        />
      </TrackerSection>

      <ComingSoonList items={comingSoon} />
    </div>
  );
};

/**
 * The headline figures, from the same payload the table below renders: the latest month's settled
 * volume, the twelve months' settled volume, and how many people the scheme bands. Sums are of
 * integer cents, converted only by the card formatter.
 */
const HeadlineCards: React.FC<{ data: BrokerMonthlyPayload }> = ({ data }) => {
  const last = data.months[data.months.length - 1];
  const lastTotal = data.totals[data.totals.length - 1];
  const twelveMonthCents = data.totals.reduce((sum, t) => sum + t.settled.valueCents, 0);
  const twelveMonthDeals = data.totals.reduce((sum, t) => sum + t.settled.count, 0);
  const bandedCount = data.brokers.filter((b) => kindOf(b) === 'broker').length;
  return (
    <StatGrid>
      <StatCard
        label={last ? `Settled, ${monthShort(last.month)}` : 'Settled, latest month'}
        value={money(lastTotal?.settled.valueCents ?? null)}
        sub={last?.partial ? `Provisional, to ${formatIsoDate(last.to)}` : `${num(lastTotal?.settled.count ?? 0)} deals`}
        icon={Handshake}
        color="#2563eb"
      />
      <StatCard
        label="Settled, twelve months"
        value={money(twelveMonthCents)}
        sub={`${num(twelveMonthDeals)} deals`}
        icon={CalendarRange}
        color="#16a34a"
      />
      <StatCard
        label="Brokers banded"
        value={num(bandedCount)}
        sub={data.bands.length > 0 ? `${data.bands.length} bands in the scheme` : 'No scheme entered'}
        icon={Users}
        color="#7c3aed"
      />
    </StatGrid>
  );
};

/** How a band applies, said once under the cards and in each card's info tip. */
export const BAND_RULE =
  "A broker sits in one band each month, set by their own settled volume. The band's split is the share of that broker's own net commission for the month, applied to the whole month. Bands reset on the 1st with no carry-over.";

/** The scheme as cards, so the reader sees the rule before the table that applies it. */
const BandCards: React.FC<{ data: BrokerMonthlyPayload }> = ({ data }) => {
  if (data.bandsInvalidReason) {
    return (
      <Honesty>
        The saved bands could not be read ({data.bandsInvalidReason}), so no band is shown. Re-enter them
        with Edit bands and costs.
      </Honesty>
    );
  }
  if (data.bands.length === 0) {
    return (
      <Honesty>
        No settlement bands have been entered yet, so no month is banded. An administrator can enter
        them with Edit bands and costs.
      </Honesty>
    );
  }
  return (
    <div className="space-y-2">
    <div className="grid gap-3 sm:grid-cols-3">
      {data.bands.map((band, i) => {
        const colour = bandColour(i);
        return (
          <div
            key={i}
            className="relative overflow-hidden rounded-lg border p-3 pt-4"
            style={{ borderColor: colour.border, background: `linear-gradient(180deg, ${colour.fill}, rgba(17,24,39,0.5) 80%)` }}
          >
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1" style={{ background: colour.bar }} />
            <div className="flex items-center justify-between gap-2">
              <span
                className="rounded-full border px-2 py-px text-[0.6875rem] font-bold uppercase tracking-wide"
                style={{ color: colour.text, borderColor: colour.border }}
              >
                Band {i + 1}
              </span>
              <InfoTip label={`Band ${i + 1}`}>{BAND_RULE}</InfoTip>
            </div>
            <div className="mt-2 text-xl font-extrabold tracking-tight" style={{ color: colour.text }}>
              {band.ratePct === 0 ? 'No commission' : `${pct(band.ratePct)} split`}
            </div>
            <div className="mt-0.5 text-xs text-gray-400">{bandRangeLabel(band.fromCents, band.toCents)} settled in the month</div>
          </div>
        );
      })}
    </div>
    <p className="text-xs leading-relaxed text-gray-400" data-testid="band-rule">{BAND_RULE}</p>
    </div>
  );
};

/**
 * Each banded broker's latest month against the scheme: the band reached, and how far the month's
 * settled volume has climbed through it toward the next. The distance left is the next band's
 * start less the volume, in cents. Only drawn when a readable scheme exists.
 */
const BandProgress: React.FC<{ data: BrokerMonthlyPayload }> = ({ data }) => {
  const last = data.months[data.months.length - 1];
  const banded = data.brokers.filter((b) => kindOf(b) === 'broker');
  if (!last || data.bandsInvalidReason || data.bands.length === 0 || banded.length === 0) return null;
  return (
    <div className="rounded-lg border border-gray-700/70 bg-gray-900/30 p-4" data-testid="band-progress">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        Progress to the next band, {monthShort(last.month)}{last.partial ? ' (provisional)' : ''}
      </h4>
      <ul className="mt-3 space-y-3">
        {banded.map((b) => {
          const m = b.months[b.months.length - 1];
          if (!m) return null;
          const band = m.band;
          let ratio = 0;
          let colour = '#64748b';
          let note: string;
          if (band.status === 'in_band') {
            colour = bandColour(band.index).bar;
            if (band.toCents === null) {
              ratio = 1;
              note = 'Top band';
            } else {
              const span = band.toCents - band.fromCents;
              ratio = span > 0 ? (m.settled.valueCents - band.fromCents) / span : 0;
              note = `${money(band.toCents - m.settled.valueCents)} to Band ${band.index + 2}`;
            }
          } else {
            note = band.status === 'above_top' ? 'Above the top band' : 'Not placed in a band';
          }
          return (
            <li key={b.adviserKey} className="grid items-center gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,12rem)_auto_minmax(0,1fr)_minmax(0,9rem)]">
              <span className="truncate text-sm text-gray-200">{b.adviserName}</span>
              <span><BandTag band={band} split /></span>
              <RatioBar ratio={ratio} color={colour} className="h-2" />
              <span className="text-xs tabular-nums text-gray-400 sm:text-right">
                {money(m.settled.valueCents)} &middot; {note}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Broker rows by month: settled volume over the band it reached. */
const BandingTable: React.FC<{ data: BrokerMonthlyPayload }> = ({ data }) => {
  const last = data.months[data.months.length - 1];
  const banded = data.brokers.filter((b) => kindOf(b) === 'broker');
  const notBanded = data.brokers.filter((b) => kindOf(b) !== 'broker');
  if (data.brokers.length === 0) {
    return <p className="text-sm text-gray-400">No settlements or submissions in these twelve months.</p>;
  }
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-gray-700/60">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/70">
            <tr className="border-b border-gray-700/70 text-left">
              <th className={TH}>Broker</th>
              {data.months.map((m) => (
                <th key={m.month} className={`${TH} text-right ${m.partial ? 'text-amber-300' : ''}`}>
                  {`${monthShort(m.month)}${m.partial ? ' *' : ''}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {banded.map((b) => (
              <VolumeRow key={`broker:${b.adviserKey}`} row={b} />
            ))}
            {notBanded.length > 0 && (
              <tr className="border-b border-gray-800/80 bg-gray-900/40">
                <td colSpan={data.months.length + 1} className="px-2.5 pb-1 pt-3 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-gray-500">
                  Not banded: counted in the total, not paid on this scheme
                </td>
              </tr>
            )}
            {notBanded.map((b) => (
              <VolumeRow key={`${kindOf(b)}:${b.adviserKey}`} row={b} />
            ))}
            <tr className="border-t-2 border-blue-500/40 bg-blue-500/[0.08] font-semibold">
              <td className="px-2.5 py-2 text-gray-100">Total settled</td>
              {data.totals.map((t) => (
                <td key={t.month} className={`${TD} text-right text-gray-100`}>{money(t.settled.valueCents)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <Honesty>
        {last?.partial && <>* {monthShort(last.month)} runs only to {formatIsoDate(last.to)}, the export date, so its band is provisional. </>}
        Settled deals with no deal value are counted but add $0 to volume. The Total row equals Settlements
        on Deal movements for the same month.
      </Honesty>
    </div>
  );
};

const KIND_NOTE: Record<Exclude<BrokerMonthsKind, 'broker'>, string> = {
  group: 'group member',
  support: 'support staff',
  unassigned: 'no owner on the deal',
};

/** One person's (or the Unassigned bucket's) settled volume by month, with the band when banded. */
const VolumeRow: React.FC<{ row: BrokerMonthsRow }> = ({ row }) => {
  const kind = kindOf(row);
  return (
    <tr className="border-b border-gray-800/80 transition-colors odd:bg-white/[0.015] hover:bg-white/[0.04]">
      <td className="whitespace-nowrap px-2.5 py-2 text-gray-200">
        {row.adviserName}
        {kind !== 'broker' && (
          <span className="ml-1.5 text-[0.6875rem] text-gray-500">
            {kind === 'group' && row.groupLabel ? row.groupLabel : KIND_NOTE[kind]}
          </span>
        )}
      </td>
      {row.months.map((m) => (
        <td key={m.month} className={`${TD} text-right align-top`}>
          <div className={m.settled.valueCents > 0 ? 'text-gray-100' : 'text-gray-500'}>{money(m.settled.valueCents)}</div>
          {kind === 'broker' && <div className="mt-0.5"><BandTag band={m.band} /></div>}
        </td>
      ))}
    </tr>
  );
};

const LoanTypeTable: React.FC<{ title: string; rows: LoanTypeRow[]; totalCents: number; totalDeals: number; colour: string }> = ({
  title, rows, totalCents, totalDeals, colour,
}) => (
  <div>
    <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
      <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: colour }} />
      {title}
    </h4>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-700/70 text-left">
          <th className={TH}>{title}</th>
          <th className={`${TH} text-right`}>Settled volume</th>
          <th className={`${TH} text-right`}>% of volume</th>
          <th className={`${TH} text-right`}>Deals</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-gray-800/80 bg-white/[0.03] font-semibold">
          <td className="px-2.5 py-2 text-gray-100">All settled deals</td>
          <td className={`${TD} text-right text-gray-100`}>{formatCents(totalCents)}</td>
          <td className={`${TD} text-right text-gray-100`}>{totalCents > 0 ? '100%' : '-'}</td>
          <td className={`${TD} text-right text-gray-100`}>{num(totalDeals)}</td>
        </tr>
        {rows.map((r) => {
          const share = totalCents > 0 && r.valueCents >= 0 ? r.valueCents / totalCents : null;
          return (
            <tr key={`${r.basis}:${r.label}`} className="border-b border-gray-800/80 transition-colors hover:bg-white/[0.04]">
              <td className={`px-2.5 py-2 ${r.basis === 'recorded' ? 'text-gray-200' : 'italic text-gray-400'}`}>{r.label}</td>
              <td className={`${TD} text-right text-gray-200`}>{formatCents(r.valueCents)}</td>
              <td className={`${TD} text-right text-gray-400`}>
                <span>{share === null ? '-' : `${(share * 100).toFixed(1)}%`}</span>
                {share !== null && <RatioBar ratio={share} color={colour} className="ml-auto mt-1 w-16" />}
              </td>
              <td className={`${TD} text-right text-gray-200`}>{num(r.count)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const LoanTypePanel: React.FC<{ data: BrokerMonthlyPayload }> = ({ data }) => {
  const lt = data.loanTypes;
  return (
    <AccentPanel
      title="Loan type breakdown"
      icon={Layers}
      color="#0891b2"
      badge={<Pill tone="brand">Tracker data</Pill>}
      blurb={`Settled deals from ${formatIsoDate(lt.from)} to ${formatIsoDate(lt.to)}, by the loan purpose and transaction type on their product splits in the Sales export.`}
    >
      {lt.status === 'no_sales_snapshot' ? (
        <Honesty>
          Loan purpose and transaction type come from the Sales export, and no Sales export was uploaded
          within a day of this Performance export. Upload both from the same day to see this breakdown.
        </Honesty>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-5 lg:grid-cols-2">
            <LoanTypeTable title="Loan purpose" rows={lt.byPurpose} totalCents={lt.settledValueCents} totalDeals={lt.settledDeals} colour="#2563eb" />
            <LoanTypeTable title="Transaction type" rows={lt.byTransactionType} totalCents={lt.settledValueCents} totalDeals={lt.settledDeals} colour="#0891b2" />
          </div>
          <Honesty>
            Each settled deal is counted once at its deal value. A deal whose splits disagree is
            &quot;Mixed&quot;, one whose splits leave the field blank is &quot;Not recorded&quot;, and one with no
            product split at all is &quot;No product split&quot;, so both tables add up to every settled deal.
            Splits from the {formatIsoDate(lt.salesAsAtDate)} Sales export.
          </Honesty>
        </div>
      )}
    </AccentPanel>
  );
};

/**
 * Costs as entered, nothing derived. Revenue needs commission data, which lives on the commission
 * pages; ROI is in the tab's "Coming when the data is available" list.
 */
const CostsPanel: React.FC<{
  costs: Record<string, RemBrokerCost> | null;
  people: ReadonlyArray<{ adviserKey: string; adviserName: string }>;
  canEdit: boolean;
}> = ({ costs, people, canEdit }) => {
  const rows = useMemo(() => {
    if (!costs) return [];
    const names = new Map(people.map((p) => [p.adviserKey, p.adviserName]));
    return Object.entries(costs)
      .map(([key, cost]) => ({ key, name: names.get(key) ?? key, cost }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [costs, people]);

  return (
    <AccentPanel
      title="Broker costs"
      icon={Wallet}
      color="#f59e0b"
      blurb="The practice's monthly base and support cost per broker, as entered. Revenue is a commission figure and lives on the commission pages."
    >
      <div className="space-y-3">
        {rows.length === 0 ? (
          <Honesty>
            No broker costs have been entered.{canEdit ? ' Enter them with Edit bands and costs above.' : ''}
          </Honesty>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-700/60">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/70">
                <tr className="border-b border-gray-700/70 text-left">
                  <th className={TH}>Broker</th>
                  <th className={`${TH} text-right`}>Base / month</th>
                  <th className={`${TH} text-right`}>Support / month</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-gray-800/80 transition-colors odd:bg-white/[0.015] hover:bg-white/[0.04]">
                    <td className="px-2.5 py-2 text-gray-200">{r.name}</td>
                    <td className={`${TD} text-right text-gray-200`}>
                      {r.cost.monthlyBaseCents === undefined ? <span className="text-gray-500">Not entered</span> : formatCents(r.cost.monthlyBaseCents)}
                    </td>
                    <td className={`${TD} text-right text-gray-200`}>
                      {r.cost.monthlySupportCents === undefined ? <span className="text-gray-500">Not entered</span> : formatCents(r.cost.monthlySupportCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AccentPanel>
  );
};

export default BrokerRemTab;
